// Hand-mirrored config + approval contracts (AL3). Surface-specific to apps/admin
// (the AL1/AL2b discipline; offline OpenAPI gen still blocked, follow-up #1). Source
// of record, kept aligned by hand:
//   apps/api/src/modules/config/config-read.controller.ts + config.controller.ts
//   apps/api/src/modules/approvals/approval.controller.ts  (the GA1 Approval API)
//   packages/validation/src/config.ts  (proposeConfigSchema, rejectConfigSchema)
//   packages/db/src/schema/registry.ts   (config_version columns + enums)
// On any ambiguous field, flag the API team — do NOT guess.

export type GovernanceLevel = "NORMAL_ADMIN" | "FOUR_EYES";
export type ConfigStatus = "DRAFT" | "PENDING_APPROVAL" | "ACTIVE" | "SUPERSEDED" | "REJECTED";

/** A config_version row. `payload` is the full namespace config (guardrail-validated
 *  server-side — never validated client-side). Timestamps are ISO strings. */
export interface ConfigVersion {
  id: string;
  namespace: string;
  version: number;
  payload: Record<string, unknown>;
  governanceLevel: GovernanceLevel;
  status: ConfigStatus;
  guardrailVersion: number;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  proposedByStaffId: string | null;
  approvedByStaffId: string | null;
  approvalId: string | null;
  changeReason: string | null;
  createdAt: string;
}

/** GET /v1/config — the dashboard. The UI branches on `governanceLevel` from HERE,
 *  never a hard-coded list. `active` is null for a namespace with no ACTIVE version. */
export interface NamespaceSummary {
  namespace: string;
  governanceLevel: GovernanceLevel;
  active: ConfigVersion | null;
}
export interface ConfigDashboard {
  namespaces: NamespaceSummary[];
}

/** GET /v1/config/:namespace — the default (no `?at=`) shape. */
export interface NamespaceDetail {
  namespace: string;
  governanceLevel: GovernanceLevel;
  active: ConfigVersion | null;
  history: ConfigVersion[];
}

/** GET /v1/config/:namespace?at=<ISO> — a DISTINCT, narrower shape: just the version
 *  active at that instant (no governanceLevel/history). Don't assume the full envelope. */
export interface NamespaceAtVersion {
  namespace: string;
  at: string;
  version: ConfigVersion;
}

/** POST /v1/config/:namespace body (proposeConfigSchema, `.strict()`). */
export interface ProposeConfigInput {
  payload: Record<string, unknown>;
  changeReason?: string;
}

// ── GA1 Approval API (the same contract the console's AC6 inbox consumes) ──
export type ApprovalStatus = "PENDING" | "APPROVED" | "REJECTED" | "CONSUMED";
/** A7 (#293) — WHERE to review the approval, resolved server-side (review-path.ts),
 *  or null when no review surface exists for the action. For config.activate the
 *  server resolves the namespace and returns an admin `/config/governance?ns=` link. */
export interface ReviewTarget {
  app: "console" | "admin";
  path: string;
}
export interface Approval {
  id: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  requestedBy: string;
  approvedBy: string | null;
  status: ApprovalStatus;
  expiresAt: string;
  createdAt: string;
  review?: ReviewTarget | null;
}

/** The four-eyes action a config activation is approved under (APPROVAL_POLICY:
 *  requestPermission config:activate, approvePermission config:approve). */
export const CONFIG_ACTIVATE_ACTION = "config.activate";
